import Button from '@components/v2/ui/Button/Button';
import Icon from '@components/v2/ui/Icon/Icon';
import Menu from '@components/v2/ui/Menu/Menu';
import MenuItem from '@components/v2/ui/Menu/MenuItem';
import Typography from '@components/v2/ui/Typography/Typography';
import { Stack } from '@mui/material';
import { ChevronDown } from 'lucide-react';
import { useMenu } from '../../../../../../../hooks/useMenu';
import LangEnum from '../../../../../../../lang/LangEnum';
import { useTranslation } from '../../../../../../translation/TranslationContext';
import { optionsMenuListSx, optionsMenuPaperSx } from '../orderProductsViewButton.styles';
import { OrderProductsViewSelectConfig } from '../orderProductsViewButton.types';

const ROW_HEIGHT = 22;
const TRIGGER_MAX_WIDTH = 176;

interface OrderProductsViewSelectRowProps {
  label: LangEnum;
  testId: string;
  config: OrderProductsViewSelectConfig;
}

const OrderProductsViewSelectRow = ({ label, testId, config }: OrderProductsViewSelectRowProps) => {
  const { translate } = useTranslation();
  const { anchorEl, isOpen, open, close } = useMenu();

  const selectedOption = config.options.find((option) => option.key === config.selectedKey);
  const selectedLabel = translate(
    selectedOption?.label ?? LangEnum.ORDER_PRODUCTS_GROUPY_BY_NONE_LABEL,
  );

  const handleSelectOption = (optionKey: string) => {
    config.onChange(optionKey);
    close();
  };

  return (
    <Stack
      direction="row"
      sx={{ height: ROW_HEIGHT, gap: 1, alignItems: 'center', justifyContent: 'space-between' }}
    >
      <Typography variant="body2" color="text.secondary" noWrap sx={{ flexShrink: 0 }}>
        {translate(label)}
      </Typography>

      <Button
        variant="contained"
        color="secondary"
        size="small"
        shape="square"
        disabled={config.isDisabled}
        testId={testId}
        endIcon={<Icon icon={ChevronDown} size="xs" />}
        sx={{ minWidth: 0, maxWidth: TRIGGER_MAX_WIDTH }}
        aria-label={`${translate(label)}: ${selectedLabel}`}
        onClick={open}
      >
        <Typography variant="inherit" noWrap title={selectedLabel}>
          {selectedLabel}
        </Typography>
      </Button>

      <Menu
        anchorEl={anchorEl}
        open={isOpen}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        slotProps={{ paper: { sx: optionsMenuPaperSx }, list: { sx: optionsMenuListSx } }}
        onClose={close}
      >
        {config.options.map((option) => (
          <MenuItem
            key={option.key}
            selected={option.key === config.selectedKey}
            testId={`${testId}-${option.key}`}
            onClick={() => handleSelectOption(option.key)}
          >
            <Typography variant="body2" noWrap title={translate(option.label)}>
              {translate(option.label)}
            </Typography>
          </MenuItem>
        ))}
      </Menu>
    </Stack>
  );
};

export default OrderProductsViewSelectRow;
